import { useCallback } from "react";
import { DeviceHelper, dispatch, PaymentStore } from "@nf/common";
import { ROUTES } from "@nf/constants";
import { useGeneratePath } from "./useGeneratePath";
import { useRouter } from "next/router";
import { UrlObject } from "url";

export const PAYMENT_PARAM = "payment";
export const SUCCESS_VALUE = "success";
export const ERROR_VALUE = "error";

export const useCreatePaymentCallback = () => {
  const generateFullUrlPath = useGeneratePath(true, true);
  const generatePath = useGeneratePath(false, false);
  const { query } = useRouter();
  const isMobile = DeviceHelper.isMobile();

  const generateFailPaymentCallbackUrl = (
    offerId?: string,
    voucherCode?: string
  ) => {
    const params: Record<string, string | undefined> = {
      [PAYMENT_PARAM]: ERROR_VALUE,
      [SUCCESS_VALUE]: "false",
      voucherCode,
      offerId,
    };
    const paramsToString = Object.keys(params)
      .map((key) =>
        params[key] ? key + "=" + encodeURIComponent(params[key] ?? "") : ""
      )
      .join("&");
    return generateFullUrlPath(`${ROUTES.PAYMENT}?${paramsToString}`);
  };

  const getFailAssignSubscriptionCallbackUrl = useCallback(
    (offerId?: string, voucherCode?: string): UrlObject => {
      const params: Record<string, string | undefined> = {
        [PAYMENT_PARAM]: ERROR_VALUE,
        voucherCode,
        offerId,
      };

      const withoutEmptyParams = Object.keys(params).reduce(
        (acc, key) => ({
          ...acc,
          ...(params[key] && { [key]: params[key] }),
        }),
        {}
      );

      return {
        pathname: generatePath(`${ROUTES.PAYMENT_DETAILS}`),
        query: withoutEmptyParams,
      };
    },
    [generateFullUrlPath, query]
  );

  const getSuccessAssignSubscriptionCallbackUrl = useCallback(() => {
    const okUrl = generatePath(isMobile ? ROUTES.WELCOME : ROUTES.HOME);
    const okURLObject: UrlObject = {
      pathname: okUrl,
    };

    if (!isMobile) {
      okURLObject.query = {
        [PAYMENT_PARAM]: SUCCESS_VALUE,
      };
    }
    return okURLObject;
  }, [generatePath, query, isMobile]);

  const getInitialPaymentCallbackUrl = useCallback(
    (offerId?: string, voucherCode?: string) => {
      const okUrl = generateFullUrlPath(
        isMobile
          ? `${ROUTES.WELCOME}?${PAYMENT_PARAM}=${SUCCESS_VALUE}`
          : `${ROUTES.PAYMENT}?success=true`
      );
      const failUrl = generateFailPaymentCallbackUrl(offerId, voucherCode);
      return {
        okUrl,
        failUrl,
      };
    },
    [generateFullUrlPath, query, isMobile]
  );

  const getUpdatePaymentCallbackUrl = useCallback(() => {
    const url = `${ROUTES.SETTINGS_PAYMENT_DETAILS}?${PAYMENT_PARAM}`;
    const okUrl = generateFullUrlPath(`${url}=${SUCCESS_VALUE}`);
    const failUrl = generateFullUrlPath(`${url}=${ERROR_VALUE}`);
    dispatch(PaymentStore.Actions.setPaymentCallback(okUrl, failUrl));
  }, [generateFullUrlPath]);

  return {
    getSuccessAssignSubscriptionCallbackUrl,
    getFailAssignSubscriptionCallbackUrl,
    getInitialPaymentCallbackUrl,
    getUpdatePaymentCallbackUrl,
  };
};
